import { FormEvent, useState } from 'react';
import { addressApi } from '../../lib/addressApi';
import { DeliveryAddress, DeliveryAddressInput } from '../../types/api';
import DeliveryAddressForm, { emptyAddress, toAddressInput } from '../Account/DeliveryAddressForm';

interface Props {
  readonly addresses: readonly DeliveryAddress[];
  readonly selectedId: string;
  readonly onSelect: (id: string) => void;
  readonly locked: boolean;
  /** Whether the add/edit form is open; the page blocks payment meanwhile. */
  readonly editing: boolean;
  readonly onEditingChange: (editing: boolean) => void;
  /** Called with the saved address id after a create or update succeeds. */
  readonly onSaved: (id: string) => Promise<void>;
  readonly onError: (message: string) => void;
}

export default function AddressPicker({
  addresses,
  selectedId,
  onSelect,
  locked,
  editing,
  onEditingChange,
  onSaved,
  onError,
}: Props) {
  const [draft, setDraft] = useState<DeliveryAddressInput>(emptyAddress);
  const [editId, setEditId] = useState('');
  const [saving, setSaving] = useState(false);

  const openEditor = (input: DeliveryAddressInput, id = '') => {
    setDraft(input);
    setEditId(id);
    onEditingChange(true);
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    onError('');
    try {
      const saved = editId ? await addressApi.replace(editId, draft) : await addressApi.create(draft);
      await onSaved(saved.id);
      onEditingChange(false);
      setEditId('');
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Could not save address');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <h2>Delivery address</h2>
      <fieldset disabled={locked}>
        {addresses.map((address) => (
          <div key={address.id}>
            <label>
              <input type="radio" checked={selectedId === address.id} onChange={() => onSelect(address.id)} />
              {address.recipient_name}, {address.address_line_1}, {address.city}, {address.state}{' '}
              {address.postal_code}
            </label>
            <button onClick={() => openEditor(toAddressInput(address), address.id)}>Edit</button>
          </div>
        ))}
        <button onClick={() => openEditor(emptyAddress)}>Add delivery address</button>
      </fieldset>
      {editing && (
        <form onSubmit={save}>
          <DeliveryAddressForm value={draft} onChange={setDraft} disabled={saving || locked} />
          <button disabled={saving}>Save address</button>
          <button type="button" onClick={() => onEditingChange(false)}>
            Cancel
          </button>
        </form>
      )}
    </section>
  );
}
